import React, { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

/**
 * SPT-PTE-2026-0062 — lightweight inline pen-edit for a job item's Sage
 * Quote Number, used in Dispatch + the Job Detail lines table so Elsa/Heike
 * can capture/correct it standalone, without going through "Mark Paid" or
 * "Capture Invoice" (which also force a payment_status change). Setting a
 * NEW quote number here (while no PO exists yet) starts the 4-working-day
 * PO follow-up countdown server-side; clearing it stops that countdown.
 */
export function InlineQuoteEdit({ item, onSaved, testidPrefix = "inline-quote" }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.quote_number || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const trimmed = value.trim();
    setBusy(true);
    try {
      await api.patch(`/job-items/${item.id}/payment`, { quote_number: trimmed || "" });
      toast.success(trimmed ? "Quote number updated" : "Quote number cleared");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        <span className="mono" style={{ fontSize: 11.5 }} data-testid={`${testidPrefix}-value-${item.id}`}>
          {item.quote_number || <span className="t-mut">—</span>}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setValue(item.quote_number || ""); setEditing(true); }}
          title="Edit quote number"
          data-testid={`${testidPrefix}-edit-${item.id}`}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: "var(--text-3)", display: "inline-flex" }}>
          <Pencil size={11} />
        </button>
      </span>
    );
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} onClick={(e) => e.stopPropagation()}>
      <input
        className="input mono"
        style={{ width: 110, fontSize: 11.5, padding: "2px 6px" }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Quote #"
        autoFocus
        disabled={busy}
        data-testid={`${testidPrefix}-input-${item.id}`}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
      />
      <button onClick={save} disabled={busy} title="Save"
        data-testid={`${testidPrefix}-save-${item.id}`}
        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--gold-deep)", display: "inline-flex" }}>
        <Check size={13} />
      </button>
      <button onClick={() => setEditing(false)} disabled={busy} title="Cancel"
        data-testid={`${testidPrefix}-cancel-${item.id}`}
        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", display: "inline-flex" }}>
        <X size={13} />
      </button>
    </span>
  );
}
